import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, AreaChart, Area, RadialBarChart, RadialBar, Legend,
} from "recharts";
import { api, formatApiError } from "@/lib/api";
import PageShell from "@/components/PageShell";
import { TrendingUp, RefreshCw, Sparkles } from "lucide-react";

const RISK_BG = {
  high: "bg-rose-100 text-rose-800 border-rose-200",
  medium: "bg-amber-100 text-amber-800 border-amber-200",
  low: "bg-emerald-100 text-emerald-800 border-emerald-200",
};

export default function PortalLeakage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = (force = false) => {
    setLoading(true);
    api.get(`/clinic/analytics/leakage${force ? "?refresh=true" : ""}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const refresh = async () => {
    setRefreshing(true);
    try { load(true); toast.success("Market summary refreshed."); }
    finally { setRefreshing(false); }
  };

  return (
    <PageShell
      eyebrow="Analytics · Competitive Leakage"
      title="See where consumers are seeking services outside the clinic."
      intro="Consumer behavior and market demand analytics. This dashboard provides market intelligence only — it does not compare clinical quality or treatment outcomes."
      testId="leakage-page"
      actions={
        <button
          onClick={refresh}
          disabled={refreshing || loading}
          data-testid="leakage-refresh"
          className="inline-flex items-center gap-1.5 bg-white border border-slate-200 text-slate-700 rounded-full px-4 py-2 text-sm font-semibold hover:bg-slate-50 disabled:opacity-60"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} /> Refresh summary
        </button>
      }
    >
      {loading ? (
        <div className="text-sm text-slate-500">Loading market intelligence…</div>
      ) : data ? (
        <>
          <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-900 to-slate-800 text-white p-6 mb-5" data-testid="leakage-summary">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-semibold text-blue-300 mb-2">
              <Sparkles className="w-3.5 h-3.5" /> Monthly intelligence summary
            </div>
            <p className="text-base text-slate-100 leading-relaxed">{data.summary_text}</p>
            <div className="text-[10px] text-slate-400 mt-3">
              Generated {data.generated_at ? new Date(data.generated_at).toLocaleString() : "—"} · cached for 7 days
            </div>
          </div>

          <div className="grid lg:grid-cols-4 gap-4 mb-5">
            <BannerSection title="Trending categories" rows={data.trending} testId="trending" />
            <BannerSection title="High consumer demand" rows={data.high_demand} testId="high-demand" />
            <BannerSection title="High leakage risk" rows={data.high_leakage} testId="high-leakage" />
            <BannerSection title="Fastest-growing segments" rows={data.fastest_growing} testId="fastest" />
          </div>

          {/* Demand index bar chart */}
          <div className="grid lg:grid-cols-2 gap-5 mb-5">
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500 mb-3">
                Cash-pay demand index (0-100)
              </div>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={data.categories}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} angle={-15} textAnchor="end" height={60} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Bar dataKey="demand_index" fill="#2563eb" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500 mb-3">
                YoY growth percentage
              </div>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={data.categories}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} angle={-15} textAnchor="end" height={60} />
                  <YAxis tick={{ fontSize: 10 }} unit="%" />
                  <Tooltip />
                  <Bar dataKey="yoy_growth_pct" fill="#16a34a" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Heat map */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500 mb-3">
              Cash-pay demand heat map
            </div>
            <div className="grid sm:grid-cols-3 lg:grid-cols-7 gap-2">
              {data.categories.map((c) => (
                <div
                  key={c.category}
                  data-testid={`leakage-heat-${c.category}`}
                  className={`rounded-xl p-3 text-center border ${RISK_BG[c.leakage_risk] || "bg-slate-50 text-slate-700 border-slate-200"}`}
                >
                  <div className="text-[10px] uppercase font-semibold opacity-90 truncate">{c.label}</div>
                  <div className="font-display text-xl font-extrabold mt-1">{c.demand_index}</div>
                  <div className="text-[10px] opacity-80 capitalize">{c.leakage_risk} leakage</div>
                  <div className="text-[10px] opacity-70 mt-0.5">+{c.yoy_growth_pct}% YoY</div>
                </div>
              ))}
            </div>
            <div className="text-[10px] text-slate-500 mt-3 leading-relaxed italic">
              Color = leakage risk. Number = demand index. Sourced from public 2024-2025 DTC healthcare market reports;
              admin-curated and refreshed monthly. Not a clinical comparison.
            </div>
          </div>
        </>
      ) : null}
    </PageShell>
  );
}

function BannerSection({ title, rows, testId }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`section-${testId}`}>
      <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500 mb-2">{title}</div>
      <ul className="space-y-2">
        {(rows || []).slice(0, 4).map((r) => (
          <li key={r.category} className="text-xs">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-slate-900 truncate">{r.label}</span>
              <span className="text-emerald-700 font-bold ml-2">+{r.yoy_growth_pct}%</span>
            </div>
            <div className="text-[10px] text-slate-500 mt-0.5">demand {r.demand_index} · {r.leakage_risk} leakage</div>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Suppress unused imports warning for shared chart pieces.
void LineChart; void Line; void AreaChart; void Area; void RadialBarChart; void RadialBar; void Legend; void TrendingUp;
